import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import type { BackendRedeemItem } from 'types/dto/redeem'; // 从 types/dto/redeem 导入
import { fetchRedeemItemDetail, redeemItem } from 'services/redeemService';
import './RedeemApply.scss';
import rightIcon from 'assets/images/right.png';
import { useNavigate } from 'react-router-dom';
import { buildCouponsAndVouchersPath } from 'utils/pathBuilder';
import FooterButtonGroup from 'components/footerButtonGroup/FooterButtonGroup';
import { showErrorNotification } from 'utils/commonUtils';
import dayjs from 'dayjs';
import { SLASH_FORMAT } from 'constants/consts';

export interface RedeemApplyProps {
  visible: boolean;
  onClose: () => void;
  item?: BackendRedeemItem;
  point: number;
  onRedeemSuccess?: () => void;
}

export type ModalState = 'initial' | 'success';

const RedeemApply: React.FC<RedeemApplyProps> = ({
  visible,
  onClose,
  item,
  point,
  onRedeemSuccess,
}) => {
  const { t, i18n } = useTranslation('redeem');
  const currentLanguage = i18n.language;
  const isEnglish = currentLanguage === 'en';
  const [modalState, setModalState] = useState<ModalState>('initial');
  const [loading, setLoading] = useState(false);
  const [itemDetail, setItemDetail] = useState<BackendRedeemItem | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (visible) {
      setItemDetail(null);
      setImageLoading(true);
      setImageError(false);
      setModalState('initial');
    }
  }, [visible]);

  // 当弹窗显示时，获取商品详情
  useEffect(() => {
    const loadItemDetail = async () => {
      if (!visible || !item) return;

      setLoading(true);
      setImageLoading(true);
      setImageError(false);
      try {
        const response: BackendRedeemItem = await fetchRedeemItemDetail(
          item.code,
        );
        setItemDetail(response);
      } catch (error) {
        console.error('Failed to fetch item detail:', error);
      } finally {
        setLoading(false);
      }
    };

    loadItemDetail();
  }, [visible, item, currentLanguage]);

  const handleClose = useCallback(() => {
    if (modalState === 'success') {
      onRedeemSuccess?.();
    } else {
      onClose();
    }
  }, [modalState, onClose, onRedeemSuccess]);

  const handleRedeem = useCallback(async () => {
    if (!item) return;

    setLoading(true);
    try {
      const costId = itemDetail?.costs[0]?.cost_id;
      const response = await redeemItem(item.code, costId ?? 0);
      // console.log('Redeem item response:', response);

      if (response.success || response) {
        setModalState('success');
      } else {
        showErrorNotification(
          t('redeemFailed'),
          response.message || t('errors.redeemFailed'),
        );
      }
    } catch (error) {
      showErrorNotification(t('redeemFailed'), error as Error);
    } finally {
      setLoading(false);
    }
  }, [item, itemDetail?.costs, t]);

  const handleDone = useCallback(() => {
    if (modalState === 'success') {
      onRedeemSuccess?.();
    } else {
      handleClose();
    }
  }, [handleClose, modalState, onRedeemSuccess]);

  const handleGoToWallet = useCallback(() => {
    navigate(buildCouponsAndVouchersPath());
  }, [navigate]);

  // 检查是否可以兑换
  const canRedeem = itemDetail && itemDetail?.costs[0]?.amount <= point;

  const getDateDisplay = () => {
    if (itemDetail?.redeemable) {
      return `${dayjs(itemDetail.redeemable.from).format(
        SLASH_FORMAT,
      )} - ${dayjs(itemDetail.redeemable.to).format(SLASH_FORMAT)}`;
    } else {
      return '-';
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      className="redeem-apply-modal"
      width={670}
    >
      {modalState === 'initial' ? (
        <div className="redeem-apply-content">
          <div className="redeem-apply-image">
            {imageLoading && (
              <Skeleton.Image active={true} className="image-skeleton" />
            )}
            {imageError && (
              <div className="image-placeholder">
                <i className="fas fa-image"></i>
              </div>
            )}
            <img
              src={isEnglish ? item?.image.en : item?.image.zh}
              onLoad={() => {
                setImageLoading(false);
                setImageError(false);
              }}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
              style={{ display: imageLoading || imageError ? 'none' : 'block' }}
            />
          </div>
          <div className="vls-section-title">
            {isEnglish ? item?.title.en : item?.title.zh}
          </div>

          <div className="redeem-apply-period-section">
            <div>{t('redemptionPeriod') || 'Redemption period:'}</div>
            <div className="period-dates">{getDateDisplay()}</div>
          </div>

          <div className="redeem-apply-terms-section">
            <div className="section-label">
              {t('termsAndConditions') || 'Terms and Conditions'}
            </div>
            <div
              className="terms-content"
              dangerouslySetInnerHTML={{ __html: (isEnglish ? itemDetail?.tnc.en : itemDetail?.tnc.zh) || '-' }}
            />
          </div>

          <FooterButtonGroup
            onCancel={handleClose}
            onContinue={handleRedeem}
            cancelText={t('cancelButton') || 'Cancel'}
            continueText={t('redeemButton') || 'Redeem'}
            isContinueDisabled={!canRedeem}
            isSubmitting={loading}
            isVerticalInMobile={false}
          />
        </div>
      ) : (
        <div className="redeem-apply-content">
          <div className="redeem-apply-image">
            {imageLoading && (
              <Skeleton.Image active={true} className="image-skeleton" />
            )}
            {imageError && (
              <div className="image-placeholder">
                <i className="fas fa-image"></i>
              </div>
            )}
            <img
              src={isEnglish ? item?.image.en : item?.image.zh}
              onLoad={() => {
                setImageLoading(false);
                setImageError(false);
              }}
              onError={() => {
                setImageLoading(false);
                setImageError(true);
              }}
              style={{ display: imageLoading || imageError ? 'none' : 'block' }}
            />
            <div className="success-checkmark">
              <img src={rightIcon} alt="success" />
            </div>
          </div>

          <div className="vls-section-title">{t('redeemSuccessful')}</div>

          <div className="redeem-success-product">
            {isEnglish ? item?.title.en : item?.title.zh}
          </div>

          <FooterButtonGroup
            onCancel={handleDone}
            onContinue={handleGoToWallet}
            cancelText={t('doneButton') || 'Done'}
            continueText={t('goToWalletButton') || 'Go to Wallet'}
            isVerticalInMobile={false}
          />
        </div>
      )}
    </Modal>
  );
};

export default RedeemApply;
